import {
  Box,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  Pause,
  Play,
  SkipBack15,
  SkipForward15,
} from '@time-line/components/icons';
import { clamp } from '@time-line/components/utils';
import dynamic from 'next/dynamic';
import type { SyntheticEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { formatDuration } from './util';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface PodcastPlayerAudioProps {
  url: string;
}

export function PodcastPlayerAudio({ url }: PodcastPlayerAudioProps) {
  const buttonBoxSize = useMemo(() => clamp(52, 56), []);
  const skipBoxSize = useMemo(() => clamp(32, 40), []);
  const [playerRef, setPlayerRef] = useState<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState<number>(0);
  const [playing, setPlaying] = useState(false);
  const [seeking, setSeeking] = useState(false);

  const formattedElapsedTime = formatDuration((played / 100) * duration);
  const formattedDuration = formatDuration(duration);

  /* NOTE: We capture the player element via an event handler rather than a ref
     because next/dynamic does not forward refs to the underlying component. */
  const handleLoadedMetadata = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const target = event.currentTarget;
      setPlayerRef(target);
      setDuration(target.duration);
    },
    [],
  );

  const handlePlay = () => {
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleSkip = (direction: 'forward' | 'backward') => {
    if (playerRef) {
      const skipDuration = direction === 'forward' ? 15 : -15;
      playerRef.currentTime += skipDuration;
    }
  };

  const handleChangeStart = () => {
    setSeeking(true);
  };

  const handleChange = (val: number) => {
    setPlayed(val);
  };

  const handleDuration = (event: SyntheticEvent<HTMLVideoElement>) => {
    const target = event.target as HTMLVideoElement;
    setDuration(target.duration);
  };

  /**
   * Chakra doesn't support step="any" so the min and max values are 0 and 100.
   * React-player's played value is a percentage, so we need to convert it accordingly.
.   */
  const handleChangeEnd = (val: number) => {
    setSeeking(false);
    if (playerRef) {
      playerRef.currentTime = (val / 100) * playerRef.duration;
    }
  };

  const handleProgress = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!seeking) {
      const target = event.target as HTMLVideoElement;
      const playedPercentage = (target.currentTime / target.duration) * 100;
      setPlayed(playedPercentage);
    }
  };

  return (
    <>
      <Stack
        width="100%"
        alignItems="center"
        direction="row"
        spacing="3xs"
        height="m"
        my="3xs"
      >
        <Text
          as="span"
          size="detail"
          flexShrink={0}
          display="block"
          fontFamily="mono"
          color={played ? 'gray.50' : 'whiteAlpha.500'}
        >
          {formattedElapsedTime}
        </Text>
        <Slider
          aria-label="Podcast Player"
          focusThumbOnChange={false}
          value={played}
          min={0}
          max={100}
          step={1}
          onChangeStart={handleChangeStart}
          onChange={handleChange}
          onChangeEnd={handleChangeEnd}
        >
          <SliderTrack bg="whiteAlpha.500" boxShadow="none">
            <SliderFilledTrack bg="gray.50" />
          </SliderTrack>
          {(played > 0 || playing) && (
            <SliderThumb
              boxSize="3xs"
              boxShadow="none"
              bgColor="transparent"
              _active={{
                transform: 'translateY(-50%) scale(1.3)',
              }}
            >
              <Box
                boxSize="3xs"
                bgColor="gray.50"
                borderRadius="full"
                flexShrink={0}
              />
            </SliderThumb>
          )}
        </Slider>
        <Text
          as="span"
          size="detail"
          flexShrink={0}
          display="block"
          fontFamily="mono"
          color="gray.50"
        >
          {formattedDuration}
        </Text>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="center"
        direction="row"
        spacing="2xs"
      >
        <IconButton
          onClick={() => handleSkip('backward')}
          aria-label="Skip back 15 seconds"
          variant="unstyled"
          icon={<SkipBack15 boxSize="2xs" color="gray.50" />}
          boxSize={skipBoxSize}
          borderRadius="full"
          flexShrink={0}
          _hover={{
            bgColor: 'whiteAlpha.200',
          }}
          _after={{
            content: '""',
            position: 'absolute',
            inset: '-3xs',
          }}
        />
        <IconButton
          onClick={handlePlayPause}
          aria-label="Play/Pause"
          variant="unstyled"
          bg="gray.50"
          boxSize={buttonBoxSize}
          borderRadius="50%"
          flexShrink={0}
          sx={{ svg: { boxSize: '20px' } }}
          icon={
            playing ? <Pause color="gray.600" /> : <Play color="gray.600" />
          }
        />
        <IconButton
          onClick={() => handleSkip('forward')}
          aria-label="Skip forward 15 seconds"
          variant="unstyled"
          icon={<SkipForward15 boxSize="2xs" color="gray.50" />}
          boxSize={skipBoxSize}
          borderRadius="full"
          flexShrink={0}
          _hover={{
            bgColor: 'whiteAlpha.200',
          }}
          _after={{
            content: '""',
            position: 'absolute',
            inset: '-3xs',
          }}
        />
      </Stack>
      <ReactPlayer
        src={url}
        controls
        height={0}
        width={0}
        playing={playing}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleProgress}
        onDurationChange={handleDuration}
        onLoadedMetadata={handleLoadedMetadata}
        style={{ display: 'none' }}
      />
    </>
  );
}
